import { useEffect, useMemo, useState } from "react";
import { getNonPoiIconColor, isAddedToCompare, useHandleError } from "../../helpers";
import { useCIFService } from "../../services";
import { POILineChart } from "../home/POILineChart";
import { useRecoilState, useRecoilValue } from "recoil";
import { cifTypeState, compareListState, ptypeState, selectedGeoNameState, selPOICategories, userCurrencyState } from "../../states";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heading, TypographyType, TypographyColor, Body, BodyType, BodyColor, Button, ButtonSize, ButtonTheme, ButtonVariant, InfoPanel } from "../../ui-elements";
import { RouteConstants } from "../../constants";
import * as Constants from '../../utils/constants/Constants';
import { AddToCompare } from "../home";
import { GeoId } from "../../types/geo.types";

interface CatchmentRHSBarProps {
    setIsPoiOverlapped: (value: boolean) => void;
}

export const CatchmentRHSBar = ({ setIsPoiOverlapped }: CatchmentRHSBarProps) => {
    const cifService = useCIFService();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get("catchment");
    const selCurrency = useRecoilValue(userCurrencyState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const compareList = useRecoilValue(compareListState);
    const { getErrorMsg } = useHandleError();
    const [cif, setCif] = useState<any>({});
    const [, setSelectedGeoName] = useRecoilState(selectedGeoNameState);
    const pType = useRecoilValue(ptypeState);

    const geoLevelForCompare = useMemo(() => {
        if (pType === "Branch") return "Branch";
        if (pType === "Partner") return "Partner";
        if (pType === "Relationship Manager") return "RelationshipManager";
    }, [pType]);

    const handleClickBtn = () => {
        navigate({
            pathname: RouteConstants.dashboard,
            search: `?${searchParams.toString()}`,
        });
    }

    const fetchBasicCif = (geo_id: GeoId) => {
        cifService.getBasicCif(geo_id)
            .then(res => setCif((prev: any) => ({ ...prev, ['basicCif']: res })))
            .catch(error => getErrorMsg(error))
    }

    const fetchPoiByYear = (geo_id: GeoId) => {
        const payload = {
            geoId: geo_id,
            ...selCategoryList,
            currency: selCurrency,
            parentgeoId: null,
        }
        cifService.getPoiByYear(payload)
            .then(res => setCif((prev: any) => ({ ...prev, ['poiByYear']: res.growthIntime?.sort((a: any, b: any) => a.date - b.date) || [], cagrText: res.cagrText })))
            .catch(error => console.log(error))
    }

    useEffect(() => {
        if (catchment && selCurrency)
            fetchBasicCif(catchment);
    }, [catchment, selCurrency]);

    useEffect(() => {
        if (catchment && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0 && selCurrency)
            fetchPoiByYear((catchment));
    }, [catchment, selCurrency, selCategoryList]);

    useEffect(() => {
        setSelectedGeoName(cif?.basicCif?.region);
        setIsPoiOverlapped(Boolean(cif?.basicCif?.overlap));
    }, [cif])

    return (
        <div className="h-100 w-100 position-relative m-0">
            {/* Header */}
            <div className="w-100 d-flex flex-column justify-content-start align-items-start padding-top-bottom-5 padding-left-right-5 gap-2 bg-white z-index-1 border-bottom m-0">
                <div className="w-100 d-flex justify-content-between">
                    <Heading
                        title={cif?.basicCif?.region}
                        type={TypographyType.h3}
                        colour={TypographyColor.primary}
                        classname='text-start w-65'
                    />
                    <AddToCompare
                        isAddedToCompare={isAddedToCompare(cif?.basicCif?.geo_id, compareList, pType)}
                        geoId={(cif?.basicCif?.geo_id)}
                        level={cif?.basicCif?.geoLevelNumber}
                        parent_id={null}
                        count={compareList?.[geoLevelForCompare!]?.length}
                        showNodeWithoutHover={true}
                        tooltipPosition="left"
                    />
                </div>
                <Heading
                    title={cif?.basicCif?.presence_details?.presenceType?.toUpperCase()}
                    type={TypographyType.h6}
                    colour={TypographyColor.secondary}
                    classname='text-start'
                />
            </div>


            <div style={{ height: '79.405vh' }} className="overflow-y-auto position-absolute m-0 p-0 w-100" >
                {/* Presence Details */}
                <div className="row w-100 d-flex justify-content-start align-items-start padding-top-bottom-5 padding-left-right-5 bg-white z-index-1 border-bottom m-0">
                    <Heading
                        title={'PRESENCE DETAILS'}
                        type={TypographyType.h6}
                        colour={TypographyColor.secondary}
                        classname='col-12 text-start p-0'
                    />
                    <div className="col-12 d-flex flex-column justify-content-start align-items-start gap-1 p-0 m-0 padding-top-4">
                        <Heading
                            title={cif?.basicCif?.presence_details?.geolocation}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start p-0'
                        />
                        <Body
                            type={BodyType.p2}
                            color={BodyColor.secondary}
                            classname="text-start p-0"
                        >
                            District
                        </Body>
                    </div>
                    <div className="col-6 d-flex flex-column justify-content-start align-items-start gap-1 p-0 padding-top-4">
                        <Heading
                            title={cif?.basicCif?.presence_details?.createdOn}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start p-0'
                        />
                        <Body
                            type={BodyType.p2}
                            color={BodyColor.secondary}
                            classname="text-start p-0"
                        >
                            Created On
                        </Body>
                    </div>
                    <div className="col-6 d-flex flex-column justify-content-start align-items-start gap-1 p-0 padding-top-4">
                        <Heading
                            title={cif?.basicCif?.presence_details?.activeSince}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start p-0'
                        />
                        <Body
                            type={BodyType.p2}
                            color={BodyColor.secondary}
                            classname="text-start p-0"
                        >
                            Active
                        </Body>
                    </div>
                </div>

                {/* Coverage */}
                <div className="row w-100 d-flex justify-content-start align-items-start padding-top-bottom-5 padding-left-right-5 bg-white z-index-1 border-bottom m-0">
                    <Heading
                        title={'COVERAGE'}
                        type={TypographyType.h6}
                        colour={TypographyColor.secondary}
                        classname='col-12 text-start p-0'
                    />
                    <div className="col-6 d-flex flex-column justify-content-start align-items-start gap-1 p-0 padding-top-4">
                        <Heading
                            title={cif?.basicCif?.presence_details?.coverage?.radius || '--'}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start p-0'
                        />
                        <Body
                            type={BodyType.p2}
                            color={BodyColor.secondary}
                            classname="text-start p-0"
                        >
                            Radius
                        </Body>
                    </div>
                    <div className="col-6 d-flex flex-column justify-content-start align-items-start gap-1 p-0 padding-top-4">
                        <Heading
                            title={cif?.basicCif?.presence_details?.coverage?.villageCount}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start p-0'
                        />
                        <Body
                            type={BodyType.p2}
                            color={BodyColor.secondary}
                            classname="text-start p-0"
                        >
                            Villages
                        </Body>
                    </div>

                    <hr className='p-0 m-0 w-100 border-dotted col-12 margin-top-bottom-4' />
                    <div className="col-6 d-flex justify-content-start align-items-start p-0 m-0 gap-2 padding-top-5 padding-bottom-3">
                        <div className="d-flex justify-content-center align-items-center rounded-circle square-icon-box geo-specific-icon margin-top-3">
                            <span className="material-symbols-outlined fs-20">group</span>
                        </div>
                        <div className="p-0 d-flex flex-column gap-1">
                            <Heading
                                title={cif?.basicCif?.totalPopulation}
                                type={TypographyType.h4}
                                colour={TypographyColor.primary}
                                classname='text-start p-0'
                            />
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname="p-0"
                            >
                                Population
                            </Body>
                        </div>
                    </div>
                    <div className="col-6 d-flex justify-content-start align-items-start p-0 m-0 gap-2 padding-top-5 padding-bottom-3">
                        <div className="d-flex justify-content-center align-items-center rounded-circle square-icon-box geo-specific-icon margin-top-3 text-center">
                            <span className="material-symbols-outlined fs-20">location_away</span>
                        </div>
                        <div className="p-0 d-flex flex-column gap-1">
                            <Heading
                                title={cif?.basicCif?.totalHouseholds}
                                type={TypographyType.h4}
                                colour={TypographyColor.primary}
                                classname='text-start p-0'
                            />
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname="p-0"
                            >
                                Households
                            </Body>
                        </div>
                    </div>
                    <div className="col-6 d-flex justify-content-start align-items-start p-0 m-0 gap-2 padding-top-5 padding-bottom-3">
                        <div className={`d-flex justify-content-center align-items-center rounded-circle square-icon-box text-center bg-ctv color-ctv  margin-top-3`}>
                            <span className="material-symbols-outlined fs-20">work</span>
                        </div>
                        <div className="p-0 d-flex flex-column gap-1">
                            <Heading
                                title={cif?.basicCif?.eh}
                                type={TypographyType.h4}
                                colour={TypographyColor.primary}
                                classname='text-start p-0'
                            />
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname="p-0 "
                            >
                                Entrepreneurial Households
                            </Body>
                        </div>
                    </div>
                    <div className="col-6 d-flex justify-content-start align-items-start p-0 m-0 gap-2 padding-top-5 padding-bottom-3">
                        <div className="d-flex justify-content-center align-items-center rounded-circle square-icon-box text-center bg-ctv color-ctv  margin-top-3">
                            <span className="material-symbols-outlined fs-20">location_chip</span>
                        </div>
                        <div className="p-0 d-flex flex-column gap-1">
                            <div className="d-flex justify-content-start align-items-center p-0">
                                <Heading
                                    title={`${cif?.basicCif?.poi}${cif?.basicCif?.overlap ? ' *' : ''}`}
                                    type={TypographyType.h4}
                                    colour={TypographyColor.primary}
                                    classname='text-start p-0'
                                />
                                <InfoPanel text='Points of Interest (PoI) refer to establishments across 50 sub-categories and 8 categories that have been identified as hotspots for Entrepreneurial Household and/or Core Transaction Value activity.' classname='color-black-7 fs-14 margin-left-2' />
                            </div>
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname="p-0"
                            >
                                Points of Interest
                            </Body>

                        </div>
                    </div>
                    {cif?.basicCif?.overlap && (
                        <div className="col-12 italics fs-9 color-black-7 p-0 m-0">
                            <Body
                                type={BodyType.p3}
                                color={BodyColor.primary}
                                classname="p-0 padding-top-2 fs-9 italics"
                            >
                                * PoIs are only partially covered for this Catchment.
                            </Body>
                        </div>
                    )}
                </div>

                {/* PoI Line Chart */}
                {cif?.poiByYear && cif?.poiByYear.length > 0 &&
                    <div className="row w-100 d-flex justify-content-start align-items-start padding-top-bottom-5 bg-white z-index-1 border-bottom m-0 p-0">
                        <Heading
                            title={'POINTS OF INTEREST'}
                            type={TypographyType.h6}
                            colour={TypographyColor.secondary}
                            classname='col-12 text-start p-0 padding-left-right-5'
                        />
                        <POILineChart data={cif?.poiByYear} cagrText={cif.cagrText} />
                    </div>
                }
            </div>

            {/* Footer */}
            <div style={{ height: '7.752vh' }} className="w-100 position-absolute padding-left-right-5 bg-white m-0 bottom-0 d-flex justify-content-center align-items-center">
                <Button
                    theme={ButtonTheme.primary}
                    variant={ButtonVariant.bordered}
                    type='button'
                    size={ButtonSize.large}
                    onClick={handleClickBtn}
                    classname=''
                >
                    Explore More
                </Button>
            </div>
        </div>
    );
}